import {
  Component,
  ViewChild,
  ElementRef,
  HostListener,
  HostBinding,
  Output,
  Input,
  EventEmitter,
  signal, OnInit, OnDestroy, ContentChildren, QueryList, effect,
} from '@angular/core';
import { DialogSize } from "./dialog-interface";
import { TabGroupComponent } from '../tab-group/tab-group.component';

@Component({
  selector: 'opac-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
})
export class DialogComponent implements OnInit, OnDestroy {
  private static openedDialogs: string[] = [];
  private static BASE_Z_INDEX = 1000;

  private previousFocusedElement: HTMLElement;
  private customHeaderObserver: MutationObserver;

  @ContentChildren(TabGroupComponent, { descendants: true }) tabGroups!: QueryList<TabGroupComponent>;

  @ViewChild('dialogEl', { static: true }) dialogEl!: ElementRef<HTMLDivElement>;
  @ViewChild('headerEl', { static: true }) headerEl!: ElementRef<HTMLDivElement>;

  // TODO Be careful with elements inside that can also be closed.
  @HostListener('window:keydown.escape', ['$event'])
  handleWindowEscape(event: KeyboardEvent) {
    event.preventDefault();
    if (DialogComponent.openedDialogs.at(-1) === this.id) {
      this.closeDialog(true);
    }
  }

  @HostBinding('class') get classAttribute() {
    return {
      small: this.size === 'small',
      medium: this.size === 'medium',
      large: this.size === 'large',
      xlarge: this.size === 'xlarge',
      full: this.size === 'full',
    };
  }

  @Input() set open(data: boolean) {
    this.isVisible.set(data);

    if (this.isVisible()) {
      this.openDialog();
    } else {
      this.closeDialog();
    }
  }
  /**
   * Determines the dialog id
   */
  @Input() id: string = Math.random().toString(16).substring(2);
  /**
   * Determines the dialog title
   */
  @Input() title: string = '';
  /**
   * Determines dialog size
   */
  @Input() size: DialogSize = 'medium';

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  readonly customHeader = signal<boolean>(false);
  readonly isInClassActive = signal<boolean>(false);
  readonly isOutClassActive = signal<boolean>(false);
  readonly isVisible = signal<boolean>(false);
  readonly zIndex = signal<number>(1000);

  constructor() {
    effect(() => {
      if (this.isVisible()) {
        this.isOutClassActive.set(false);
        this.isInClassActive.set(false);
        this.isInClassActive.set(true);
      } else {
        this.isOutClassActive.set(true);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    this.customHeaderObserver = new MutationObserver((elements) => {
      for (const element of elements) {
        if (element.type === 'childList') {
          this.customHeader.set(this.headerEl.nativeElement.querySelector('[slot="header"]') !== null);
        }
      }
    });

    this.customHeaderObserver.observe(this.headerEl.nativeElement, { childList: true });
  }

  ngOnDestroy() {
    this.customHeaderObserver.disconnect();
  }

  openDialog() {
    // (this.dialogEl?.nativeElement as any).showModal();
    this.isVisible.set(true);
    this.previousFocusedElement = document.activeElement as HTMLElement;

    this.dialogEl?.nativeElement.scrollTo({ top: 0 });
    DialogComponent.openedDialogs.push(this.id);

    this.zIndex.set(DialogComponent.BASE_Z_INDEX + DialogComponent.openedDialogs.length * 10);

    document.documentElement.style.overflow = 'hidden';

    this.opened.emit();

    setTimeout(() => {
      this.focusFirstFocusableElement();
    }, 1);
  }

  closeDialog(emit: boolean = false) {
    // (this.dialogEl?.nativeElement as any).close();
    this.isVisible.set(false);

    // Old behaviour closes dialog manually listening to closed event
    if (emit) {
      this.closed.emit();
      return;
    }

    DialogComponent.openedDialogs.pop();

    this.previousFocusedElement?.focus?.();

    if (DialogComponent.openedDialogs.length === 0) {
      document.documentElement.style.overflow = 'visible';
    }
  }

  focus() {
    this.focusFirstFocusableElement();
  }

  private focusFirstFocusableElement() {
    const autofocusElement = this.dialogEl.nativeElement.querySelector<HTMLElement>('[autofocus]');

    if (autofocusElement) {
      autofocusElement.focus?.();
      return;
    }

    this.dialogEl.nativeElement.querySelector<HTMLElement>('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])').focus?.();

    if (this.tabGroups) {
      this.tabGroups.forEach((tabGroup) => {
        tabGroup.refreshIndicatorPosition();
      });
    }
  }
}
